import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import io from 'socket.io-client';

import '../styles/chat.css'
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';

import { MainContainer, ChatContainer, MessageList, MessageInput, TypingIndicator, ConversationHeader } from '@chatscope/chat-ui-kit-react';
import { MessageCircle } from 'lucide-react';

import ChatViewBtn from '../components/chat/chatViewBtn';
import MainLayout from '../layouts/mainLayout';

let socket;

const initializeSocket = (token = null) => {
  if (!socket) {
    // localhost:5000 or go-condo-api-anfdc5h7fvdydqbs.australiasoutheast-01.azurewebsites.net
    socket = io('go-condo-api-anfdc5h7fvdydqbs.australiasoutheast-01.azurewebsites.net', {
      auth: {
        token: localStorage.getItem('accessToken'),
      },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect_error', (err) => console.error('Connection Error:', err));
    socket.on('error', (err) => console.error('Socket Error:', err));
    socket.on('connect', () => console.log('Connected to the server!'));
    socket.on('connected', (data) => console.log(data.message));
    socket.on('disconnect', () => console.log('Disconnected from the server'));
  }
  return socket;
};

const ChatPage = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const [activeFormMessageId, setActiveFormMessageId] = useState(null);
  const [handledForms, setHandledForms] = useState([]);
  const [formType, setFormType] = useState(null);

  const triggerWords = ["gate pass", "service request", "work permit"];

  const suggestedQuestions = [
    "What are the most recent announcements?",
    "How do I process a maintenance request?",
    "How much do I currently owe?",
  ];

  useEffect(() => {
    const socket = initializeSocket();
    // Listen for bot replies
    const handleBotReply = (botMessage) => {
      const newBotMessage = {
        id: Date.now() + 1,
        message: botMessage.text || '',  // Use botMessage.text if that's what you're sending
        sender: 'Ellie AI',
        direction: 'incoming',
        sentTime: new Date().toLocaleTimeString(),
      };

      setMessages((prev) => [...prev, newBotMessage]);
      setIsTyping(false);
    };

    socket.on('chatReply', handleBotReply);

    // Cleanup socket on unmount
    return () => {
      socket.off('chatReply', handleBotReply);
    };
  }, []);

  const handleSuggestedClick = (question) => {
    if (!isInitialized) setIsInitialized(true);

    const userMessage = {
      id: Date.now(),
      message: question,
      sender: 'user',
      direction: 'outgoing',
      sentTime: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);
    socket.emit('chatMessage', userMessage);
  };

  const sendMessage = () => {
    if (input.trim() === '') return;
    if (!isInitialized) setIsInitialized(true);

    const userMessage = {
      id: Date.now(),
      message: input,
      sender: 'user',
      direction: 'outgoing',
      sentTime: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);
    socket.emit('chatMessage', userMessage);
    setInput('');
  };

  const handleOpenForm = (messageId, messageText) => {
    setActiveFormMessageId(messageId);

    if (messageText.toLowerCase().includes("gate pass")) {
      setFormType("gate pass");
    } else if (messageText.toLowerCase().includes("service request")) {
      setFormType("service request");
    } else if (messageText.toLowerCase().includes("work permit")) {
      setFormType("work permit");
    }
  };

  const handleCloseForm = () => {
    setActiveFormMessageId(null);
  };

  const handleConfirmForm = (data) => {
    setActiveFormMessageId(null);
    setHandledForms((prev) => [...prev, activeFormMessageId]);  // mark this form as handled
    console.log("Form confirmed with data:", data);
  };

  const handleCancelForm = () => {
    setActiveFormMessageId(null);
    setHandledForms((prev) => [...prev, activeFormMessageId]);  // mark this form as handled
  };

  return (
    <MainLayout
      showChatForm={!!activeFormMessageId}
      onCloseForm={handleCloseForm}
      onConfirmForm={handleConfirmForm}
      onCancelForm={handleCancelForm}
      formType={formType}
    >
      <div className="chat-page flex justify-center items-center h-[calc(100vh-35px)] px-0 md:px-2 w-full">
        <div className="chat-container w-full  h-full rounded-2xl overflow-hidden">
          <MainContainer
            style={{
              borderRadius: '15px',
              border: 'none',
              backgroundColor: '#445485',
            }}
          >
            {!isInitialized && (
              <div className="chat-intro p-10 md:p-12 text-white font-poppins h-[500px]">
                <MessageCircle className="w-10 h-10 sm:w-16 sm:h-16 text-white mb-4" />

                <p className="chat-intro-text text-lg sm:text-2xl font-bold text-left sm:w-2/3">
                  I'm{' '}
                  <span className="ellie-highlight bg-[#6578B2] px-2 py-1 rounded">
                    Ellie
                  </span>
                  , your helpful assistant for condo living! What can I help with?
                </p>

                <p className="pt-6 pb-2 text-sm sm:text-base">
                  Here are some common questions to get you started...
                </p>

                <div className="space-y-2">
                  {suggestedQuestions.map((q, idx) => (
                    <p key={idx}>
                      <span
                        className="chat-question inline-block bg-[#69769D] hover:bg-[#6578B2] text-white px-3 py-1 rounded-lg cursor-pointer text-sm sm:text-base"
                        onClick={() => handleSuggestedClick(q)}
                      >
                        {q}
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            )}

            <ChatContainer>
              {isInitialized && (
                <ConversationHeader style={{ backgroundColor: '#6578B2', borderBottom: 'none' }}>
                  <ConversationHeader.Content>
                    <div className="chat-header flex flex-row justify-between p-2">
                      <span className="ellie-name text-white font-bold text-lg sm:text-xl text-center font-poppins w-full">
                        Ellie AI
                      </span>
                    </div>
                  </ConversationHeader.Content>
                </ConversationHeader>
              )}

              <MessageList
                typingIndicator={
                  isInitialized && isTyping && (
                    <TypingIndicator content="Ellie is typing..." style={{ backgroundColor: '#445485', marginLeft: '20px' }} />
                  )
                }
                style={{ backgroundColor: '#445485', paddingBottom: isInitialized && isTyping ? '40px' : '0px' }}
              >
                {messages.map((message) => {
                  const shouldShowBtn =
                    message.sender !== 'user' &&
                    triggerWords.some(word => message.message.toLowerCase().includes(word)) &&
                    !handledForms.includes(message.id);

                  return (
                    <div key={message.id} className={`flex flex-col ${message.sender === 'user' ? 'items-end ml-3' : 'items-start ml-3'} mb-2 mr-3`}>
                      <div className="text-xs text-gray-400 mb-1">
                        {message.sender === 'user' ? 'You' : 'Ellie'}
                      </div>

                      {shouldShowBtn && (
                        <div className="mb-2">
                          <ChatViewBtn
                            disabled={activeFormMessageId === message.id}
                            onClick={() => handleOpenForm(message.id, message.message)}
                          />
                        </div>
                      )}

                      <div className={`rounded-lg px-4 py-2 min-w-xs max-w-[80%] ${message.sender === 'user' ? 'bg-[#69769D] text-white' : 'bg-[#D6D9E8] text-gray-800'}`}>
                        <ReactMarkdown>{message.message}</ReactMarkdown>
                      </div>
                    </div>
                  );
                })}
              </MessageList>

              <MessageInput
                placeholder="Type your questions here..."
                value={input}
                onChange={setInput}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    sendMessage();
                  }
                }}
                style={{
                  fontSize: '16px',
                  padding: '10px',
                  borderTop: 'none',
                  backgroundColor: '#445485',
                }}
                attachButton={false}
              />
            </ChatContainer>
          </MainContainer>
        </div>
      </div>
    </MainLayout>
  );
};

export default ChatPage;
